import React from 'react';
import { format, parseISO, differenceInDays } from 'date-fns';
import {
  scrollToFirstError,
  focusPageHeader,
  scrollTo,
} from 'platform/utilities/ui';
import { $ } from 'platform/forms-system/src/js/utilities/ui';

/**
 * Determines whether an end date is on or after a start date.
 *
 * @param {string} endDate - The end date in ISO format (YYYY-MM-DD)
 * @param {string} startDate - The start date in ISO format (YYYY-MM-DD)
 * @returns {boolean} True if endDate is on or after startDate
 *
 * @example
 * checkIfEndDateAfterStartDate('2021-01-01', '2020-01-01') // true
 * checkIfEndDateAfterStartDate('2019-01-01', '2020-01-01') // false
 */
export const checkIfEndDateAfterStartDate = (endDate, startDate) => {
  const end = new Date(endDate);
  const start = new Date(startDate);
  return end.getTime() >= start.getTime();
};

/**
 * Determines whether an SSN is duplicated among the claimant's household SSNs.
 *
 * @param {string} value - The SSN being checked
 * @param {object} formData - The current form data
 * @param {string} [formData.childSocialSecurityNumber] - Child's SSN
 * @param {object} [formData.veteranSocialSecurityNumber] - Veteran's SSN object
 * @param {string} [formData.veteranSocialSecurityNumber.ssn] - Veteran's SSN
 * @param {string} [formData.yourSocialSecurityNumber] - Claimant's SSN
 * @returns {boolean} True if value matches more than one SSN in the household
 *
 * @example
 * isSsnDuplicated('123456789', { childSocialSecurityNumber: '123456789', yourSocialSecurityNumber: '123456789' }) // true
 */
export const isSsnDuplicated = (value, formData) => {
  const normalizeSsn = ssn => ssn?.replace(/\D/g, '');
  const childSsn = formData?.childSocialSecurityNumber;
  const veteranSsn = formData?.veteranSocialSecurityNumber?.ssn;
  const yourSsn = formData?.yourSocialSecurityNumber;

  const ssns = [childSsn, veteranSsn, yourSsn]
    .filter(Boolean)
    .map(normalizeSsn);
  const normalizedValue = normalizeSsn(value);
  return ssns.filter(ssn => ssn === normalizedValue).length > 1;
};

/**
 * Formats an ISO date string for display.
 *
 * @param {string|null|undefined} dateStr - The date in ISO format (YYYY-MM-DD)
 * @returns {string|null} The date formatted as MM/dd/yyyy, or null if dateStr is falsy
 *
 * @example
 * transformDate('2020-01-15') // '01/15/2020'
 * transformDate(null)         // null
 */
export const transformDate = dateStr => {
  if (!dateStr) return null;
  const date = parseISO(dateStr);
  return format(date, 'MM/dd/yyyy');
};

/**
 * Formats a numeric currency value for display.
 *
 * - Whole dollar amounts are displayed without cents (e.g. 1000 -> '$1,000')
 * - Amounts with cents are displayed with exactly 2 decimal places (e.g. 10.5 -> '$10.50')
 * - null or undefined values return a fallback string
 *
 * @param {number|null|undefined} amount - The numeric currency value to format
 * @returns {string|null} The formatted currency string (e.g. '$1,000' or '$10.50'),
 *   or null if amount is null or undefined. Use || to provide a fallback:
 *   `formatCurrency(item?.paymentAmount) || 'Amount not provided'`
 *
 * @example
 * formatCurrency(1000)      // '$1,000'
 * formatCurrency(10.5)      // '$10.50'
 * formatCurrency(1234567.89) // '$1,234,567.89'
 * formatCurrency(null)      // null
 * formatCurrency(undefined) // null
 */
export const formatCurrency = amount => {
  if (amount == null) return null;
  return `$${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

export const seriouslyDisabledDescription = (
  <div className="vads-u-padding-y--1">
    <va-additional-info
      trigger="What we consider a seriously disabled adult child"
      class="vads-u-margin-bottom--4"
    >
      <p>
        A child is seriously disabled if they developed a permanent physical or
        mental disability before they turned 18 years old. A seriously disabled
        child can’t support or care for themselves.
      </p>
    </va-additional-info>
  </div>
);

/**
 * Formats a full name from the given first, middle, last, and suffix
 *
 * @export
 * @param {*} {
 *   first = '',
 *   middle = '',
 *   last = '',
 *   suffix = '',
 * }
 * @return {string} The full name formatted with spaces
 */
export const formatFullName = ({
  first = '',
  middle = '',
  last = '',
  suffix = '',
}) => {
  // ensure that any middle initials are capitalized
  const formattedMiddle = middle
    ? middle.replaceAll(/\b\w\b/g, c => c.toUpperCase())
    : '';
  return [first, formattedMiddle, last, suffix]
    .filter(name => !!name)
    .join(' ');
};

export function isHomeAcreageMoreThanTwo(formData) {
  return (
    formData.homeOwnership === true && formData.homeAcreageMoreThanTwo === true
  );
}

// helper to handle both boolean and string "yes" values coming from different
// test fixtures or UX components
export const isYes = val =>
  val === true ||
  (typeof val === 'string' && val.toLowerCase().startsWith('y'));

/**
 * Calculates the duration in days between two dates
 *
 * @export
 * @param {string} startDate - The start date in ISO format (YYYY-MM-DD)
 * @param {string} endDate - The end date in ISO format (YYYY-MM-DD)
 * @return {number} The duration in days between the two dates
 */
export const durationInDays = (startDate, endDate) => {
  return differenceInDays(new Date(endDate), new Date(startDate));
};

/**
 * Determines whether expense pages should be skipped based on form data.
 *
 * Returns true (skip) only when all of the following are true:
 * - The 2025 version feature flag is enabled
 * - The claimant is claiming survivors pension
 * - The claimant reported no income (`incomeSourcesCount === 'NO_INCOME'`)
 *
 * In all other cases, expense pages should be shown.
 *
 * @param {object} formData - The current form data
 * @param {boolean} [formData.survivorsBenefitsForm2025VersionEnabled] - Feature flag for 2025 form version
 * @param {object} [formData.claims] - Benefit claims
 * @param {boolean} [formData.claims.survivorsPension] - Whether surviving pension is claimed
 * @param {string} [formData.incomeSourcesCount] - Income source selection (e.g. 'NO_INCOME', 'ONE_TO_FOUR_SOURCES', 'MORE_THAN_FIVE_SOURCES')
 * @returns {boolean} True if expense pages should be skipped, false otherwise
 */
export const shouldSkipExpensePages = formData =>
  formData?.survivorsBenefitsForm2025VersionEnabled &&
  formData?.claims?.survivorsPension === true &&
  formData?.incomeSourcesCount === 'NO_INCOME';

/**
 * Determines whether income pages should be skipped based on form data.
 *
 * Returns true (skip) only when all of the following are true:
 * - The 2025 version feature flag is enabled
 * - The claimant reported no income (`incomeSourcesCount === 'NO_INCOME'`)
 *
 * In all other cases, income pages should be shown.
 *
 * @param {object} formData - The current form data
 * @param {boolean} [formData.survivorsBenefitsForm2025VersionEnabled] - Feature flag for 2025 form version
 * @param {string} [formData.incomeSourcesCount] - Income source selection (e.g. 'NO_INCOME', 'ONE_TO_FOUR_SOURCES', 'MORE_THAN_FIVE_SOURCES')
 * @returns {boolean} True if income pages should be skipped, false otherwise
 */
export const shouldSkipIncomePages = formData =>
  formData?.survivorsBenefitsForm2025VersionEnabled &&
  formData?.incomeSourcesCount === 'NO_INCOME';

/**
 * Focuses the form-level error alert when a field error is present.
 *
 * @param {number} _index - Unused step index argument to maintain shared helper signature
 * @param {HTMLElement|Document} root - Root node to search for field-level errors
 * @returns {boolean} True when an error was found and focus/scroll behavior was triggered
 */
export const focusFirstError = (_index, root) => {
  const error = $('[error], .usa-input-error', root);
  if (error) {
    scrollToFirstError({ focusOnAlertRole: true });
    return true;
  }
  return false;
};

/**
 * Scrolls to the top of the page and focuses the main heading when no
 * field-level error is found.
 *
 * @param {number} index - Step index used by form-system helpers
 * @param {HTMLElement|Document} root - Root node to search for field-level errors
 */
export const focusH3 = (index, root) => {
  scrollTo('topContentElement');
  if (!focusFirstError(index, root)) {
    focusPageHeader(3);
  }
};

/**
 * Determines the relationship of the claimant to the Veteran.
 *
 * @param {object} data - The current form data
 * @returns {string|null} The relationship type ('child', 'spouse', 'custodian') or null if not found
 */
export const isRelationshipToVeteran = data => {
  const relationship = data?.claimantRelationship;
  if (
    relationship === 'HELPLESS_ADULT_CHILD' ||
    relationship === 'CHILD_18-23_IN_SCHOOL'
  ) {
    return 'child';
  }
  if (relationship === 'SURVIVING_SPOUSE') {
    return 'spouse';
  }
  if (relationship === 'CUSTODIAN_FILING_FOR_CHILD_UNDER_18') {
    return 'custodian';
  }
  return null;
};

/**
 * Determines if any children do not live with the claimant.
 *
 * @param {object} data - The current form data
 * @returns {boolean} False if no children or all children live with the claimant,
 * true if at least one child does not live with the claimant
 */
export const doAnyChildrenNotLiveWith = data => {
  if (data.veteranChildrenCount > 0 && data?.veteransChildren?.length > 0) {
    return data?.veteransChildren?.some(
      el => el.childLivesWith === 'NO' || el.nonChildLivesWith === false,
    );
  }
  return false;
};
